import React from 'react';
import { Badge, Button, Group, Paper, Text } from '@mantine/core';
import { IconFilter, IconX } from '@tabler/icons-react';

export interface GanttColumnFilterStripProps {
  /** Direct-matching task count (excludes ancestor-only rows). */
  matchCount: number;
  /** Labels of columns that currently have an active filter. */
  activeFilterLabels: string[];
  onClear: () => void;
}

/** Full-width strip above the Gantt when column filters are applied. */
export function GanttColumnFilterStrip({
  matchCount,
  activeFilterLabels,
  onClear,
}: GanttColumnFilterStripProps) {
  const countLabel =
    matchCount === 0
      ? 'No matching tasks'
      : `${matchCount.toLocaleString()} matching task${matchCount === 1 ? '' : 's'}`;

  return (
    <Paper
      withBorder
      radius="md"
      px="sm"
      py={6}
      style={{
        flexShrink: 0,
        borderColor:
          'color-mix(in srgb, var(--mantine-color-brand-6) 40%, var(--mantine-color-default-border))',
        background:
          'color-mix(in srgb, var(--mantine-color-brand-6) 10%, var(--mantine-color-body))',
      }}
    >
      <Group justify="space-between" wrap="nowrap" gap="sm" align="center">
        <Group gap={8} wrap="wrap" align="center" style={{ minWidth: 0 }}>
          <Group gap={6} wrap="nowrap" align="center">
            <IconFilter
              size={14}
              stroke={1.75}
              style={{ color: 'var(--mantine-color-brand-6)', flexShrink: 0 }}
              aria-hidden
            />
            <Text size="xs" fw={700} c="brand">
              Filters applied
            </Text>
          </Group>
          <Text size="xs" fw={600} c="dimmed">
            {countLabel}
          </Text>
          {activeFilterLabels.length > 0 ? (
            <Group gap={4} wrap="wrap">
              {activeFilterLabels.map((label) => (
                <Badge
                  key={label}
                  size="xs"
                  variant="light"
                  color="brand"
                  radius="sm"
                  tt="none"
                >
                  {label}
                </Badge>
              ))}
            </Group>
          ) : null}
        </Group>
        <Button
          size="compact-xs"
          variant="subtle"
          color="brand"
          leftSection={<IconX size={12} stroke={1.75} />}
          onClick={onClear}
          style={{ flexShrink: 0 }}
        >
          Clear filters
        </Button>
      </Group>
    </Paper>
  );
}
